/* ═══════════════════════════════════════════════════════════════════════════════
   TourSpotlight: Full-viewport overlay with animated cutout
   
   Uses box-shadow technique to create a "hole" around the target element.
   Auto-scrolls target into view and tracks position via ResizeObserver.
   
   FIX: For interactive steps, the target element is elevated above the overlay
   via inline z-index so clicks pass through correctly.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { SpotlightRect } from './tourSpotlightUtils';

interface TourSpotlightProps {
  targetSelector: string;
  padding?: number;
  isInteractive?: boolean;
  onOverlayClick?: () => void;
  children?: React.ReactNode;
}

export function TourSpotlight({
  targetSelector,
  padding = 12,
  isInteractive = false,
  onOverlayClick,
  children,
}: TourSpotlightProps) {
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const rafRef = useRef<number>(0);
  const targetElRef = useRef<Element | null>(null);

  const measure = useCallback(() => {
    const el = document.querySelector(targetSelector);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      x: r.x - padding,
      y: r.y - padding,
      width: r.width + padding * 2,
      height: r.height + padding * 2,
    });
  }, [targetSelector, padding]);

  // Initial measurement + scroll into view
  useEffect(() => {
    const el = document.querySelector(targetSelector);
    targetElRef.current = el;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      // Measure after scroll settles
      const timeout = setTimeout(measure, 400);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [targetSelector, measure]);

  // ─── Elevate target for interactive steps ─────────────────────────────────
  // This is the critical z-index fix: during interactive steps, the target
  // element must sit ABOVE the overlay so clicks actually reach it.
  useEffect(() => {
    const el = targetElRef.current as HTMLElement | null;
    if (!el || !isInteractive) return;

    const originalPosition = el.style.position;
    const originalZIndex = el.style.zIndex;
    const originalPointerEvents = el.style.pointerEvents;

    el.style.position = 'relative';
    el.style.zIndex = '10001'; // Above overlay (10000)
    el.style.pointerEvents = 'auto';

    return () => {
      el.style.position = originalPosition;
      el.style.zIndex = originalZIndex;
      el.style.pointerEvents = originalPointerEvents;
    };
  }, [isInteractive, targetSelector]);

  // Track position changes
  useEffect(() => {
    const el = document.querySelector(targetSelector);
    if (!el) return;

    // ResizeObserver for size changes
    observerRef.current = new ResizeObserver(() => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    });
    observerRef.current.observe(el);

    // Scroll listener for position changes
    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      observerRef.current?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [targetSelector, measure]);

  return (
    <AnimatePresence>
      <motion.div
        className="tour-spotlight-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={onOverlayClick}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          cursor: onOverlayClick ? 'pointer' : 'default',
        }}
      >
        {/* Dark overlay with cutout via box-shadow */}
        {rect && (
          <motion.div
            className="tour-spotlight-hole"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height,
            }}
            transition={{
              x: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
              y: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
              width: { duration: 0.3 },
              height: { duration: 0.3 },
              opacity: { duration: 0.2 },
            }}
            style={{
              position: 'fixed',
              borderRadius: 12,
              boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.5)',
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Spotlight ring glow */}
        {rect && (
          <motion.div
            className="tour-spotlight-ring"
            animate={{
              x: rect.x - 2,
              y: rect.y - 2,
              width: rect.width + 4,
              height: rect.height + 4,
            }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            style={{
              position: 'fixed',
              borderRadius: 14,
              border: '2px solid rgba(30, 115, 190, 0.45)',
              boxShadow: '0 0 16px rgba(30, 115, 190, 0.2), inset 0 0 16px rgba(30, 115, 190, 0.06)',
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Tooltip content: only if children provided (legacy support) */}
        {children && (
          <div
            style={{ pointerEvents: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
